import type { Rng } from '../../core/rng';
import type { Matrix4 } from 'three';

const BOB_AMPLITUDE_MIN = 0.08;
const BOB_AMPLITUDE_MAX = 0.2;
const BOB_PERIOD_MIN = 4;
const BOB_PERIOD_MAX = 7;
const ROLL_AMPLITUDE = (1.5 * Math.PI) / 180;
const PITCH_AMPLITUDE = (0.7 * Math.PI) / 180;
const TWO_PI = Math.PI * 2;

export interface ShipMotionParams {
  readonly bobAmplitude: number;
  readonly bobPeriod: number;
  readonly bobPhase: number;
  readonly rollPhase: number;
  readonly pitchPhase: number;
}

export function generateMotionParams(rng: Rng): ShipMotionParams {
  return {
    bobAmplitude: rng.range(BOB_AMPLITUDE_MIN, BOB_AMPLITUDE_MAX),
    bobPeriod: rng.range(BOB_PERIOD_MIN, BOB_PERIOD_MAX),
    bobPhase: rng.next() * Math.PI * 2,
    rollPhase: rng.next() * Math.PI * 2,
    pitchPhase: rng.next() * Math.PI * 2,
  };
}

export function computeMotion(
  params: ShipMotionParams,
  timeSeconds: number
): { readonly dy: number; readonly roll: number; readonly pitch: number } {
  const t = timeSeconds;
  const phase = (2 * Math.PI * t) / params.bobPeriod;

  const dy = params.bobAmplitude * Math.sin(phase + params.bobPhase);
  const roll = ROLL_AMPLITUDE * Math.sin(phase * 0.8 + params.rollPhase);
  const pitch = PITCH_AMPLITUDE * Math.sin(phase * 1.1 + params.pitchPhase);

  return { dy, roll, pitch };
}

export interface ShipInstance {
  readonly index: number;
  readonly baseX: number;
  readonly baseY: number;
  readonly baseZ: number;
  readonly heading: number;
  readonly motionParams: ShipMotionParams;
  readonly lengthScale?: number;
  readonly beamScale?: number;
}

/*
 * Writes the pose of a ship at `timeSeconds` into `te`, column-major as Matrix4 holds it: the
 * heading about +Y, then pitch about the ship's beam and roll about its keel, lifted by the bob.
 * It writes in place, so a frame's update allocates nothing.
 */
export function writeShipPose(
  te: Matrix4['elements'],
  inst: ShipInstance,
  timeSeconds: number
): void {
  const { bobAmplitude, bobPeriod, bobPhase, rollPhase, pitchPhase } = inst.motionParams;
  const phase = (TWO_PI * timeSeconds) / bobPeriod;

  const dy = bobAmplitude * Math.sin(phase + bobPhase);
  const roll = ROLL_AMPLITUDE * Math.sin(phase * 0.8 + rollPhase);
  const pitch = PITCH_AMPLITUDE * Math.sin(phase * 1.1 + pitchPhase);

  const cosH = Math.cos(inst.heading);
  const sinH = Math.sin(inst.heading);
  const cosR = Math.cos(roll);
  const sinR = Math.sin(roll);
  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  const lengthScale = inst.lengthScale ?? 1;
  const beamScale = inst.beamScale ?? 1;

  te[0] = (cosH * cosR + sinH * sinP * sinR) * lengthScale;
  te[1] = cosP * sinR * lengthScale;
  te[2] = (-sinH * cosR + cosH * sinP * sinR) * lengthScale;
  te[3] = 0;
  te[4] = -cosH * sinR + sinH * sinP * cosR;
  te[5] = cosP * cosR;
  te[6] = sinH * sinR + cosH * sinP * cosR;
  te[7] = 0;
  te[8] = sinH * cosP * beamScale;
  te[9] = -sinP * beamScale;
  te[10] = cosH * cosP * beamScale;
  te[11] = 0;
  te[12] = inst.baseX;
  te[13] = inst.baseY + dy;
  te[14] = inst.baseZ;
  te[15] = 1;
}
